/**
 * CR V3 Join Gate sessionStorage 契约（D10）
 */

export interface CrV3JoinSessionData {
  displayName: string
  /** 收听语种短码（WS join / UI） */
  languageCode: string
  /** 收听语种 BCP（SDK / requestSpeechToken） */
  languageCode2: string
  /** join-info workId；用于拉取本场冻结 runtime */
  workId?: string
  ts: number
}

export type CrV3JoinSessionWriteInput = Pick<
  CrV3JoinSessionData,
  'displayName' | 'languageCode' | 'languageCode2'
> & {
  workId?: string
  ts?: number
}

const DISPLAY_NAME_MIN = 1
const DISPLAY_NAME_MAX = 32

/** sessionStorage 键：cr_v3_join:{meetingCode} */
export function joinSessionStorageKey(meetingCode: string): string {
  return `cr_v3_join:${meetingCode}`
}

/** null = 合法；string = 用户可见错误文案 */
export function validateDisplayName(name: string): string | null {
  const trimmed = name.trim()
  if (trimmed.length < DISPLAY_NAME_MIN) {
    return '请输入姓名'
  }
  if (trimmed.length > DISPLAY_NAME_MAX) {
    return '姓名不能超过32个字符'
  }
  return null
}

export function readJoinSession(meetingCode: string): CrV3JoinSessionData | null {
  try {
    const raw = sessionStorage.getItem(joinSessionStorageKey(meetingCode))
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<CrV3JoinSessionData>
    if (
      typeof parsed.displayName !== 'string'
      || typeof parsed.languageCode !== 'string'
      || typeof parsed.languageCode2 !== 'string'
      || typeof parsed.ts !== 'number'
    ) {
      return null
    }
    // D10：非法 displayName（空/超长）视为无 session，强制经 Join Gate 重新提交
    if (validateDisplayName(parsed.displayName) !== null) {
      return null
    }
    return {
      displayName: parsed.displayName.trim(),
      languageCode: parsed.languageCode,
      languageCode2: parsed.languageCode2,
      ...(typeof parsed.workId === 'string' && parsed.workId
        ? { workId: parsed.workId }
        : {}),
      ts: parsed.ts,
    }
  } catch {
    return null
  }
}

export function writeJoinSession(meetingCode: string, data: CrV3JoinSessionWriteInput): void {
  const payload: CrV3JoinSessionData = {
    displayName: data.displayName.trim(),
    languageCode: data.languageCode,
    languageCode2: data.languageCode2,
    ...(data.workId ? { workId: data.workId } : {}),
    ts: data.ts ?? Date.now(),
  }
  sessionStorage.setItem(joinSessionStorageKey(meetingCode), JSON.stringify(payload))
}

export function clearJoinSession(meetingCode: string): void {
  sessionStorage.removeItem(joinSessionStorageKey(meetingCode))
}

export function hasJoinSession(meetingCode: string): boolean {
  return readJoinSession(meetingCode) !== null
}

/** Vue composable：暴露 Join sessionStorage 读写 API */
export function useCrV3JoinSession() {
  return {
    joinSessionStorageKey,
    validateDisplayName,
    readJoinSession,
    writeJoinSession,
    clearJoinSession,
    hasJoinSession,
  }
}
